import React from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Box,
} from "@mui/material";
import { Controller, useWatch, useFormState } from "react-hook-form";

const PEST_OPTIONS = ["Roaches", "Mosq", "Rodents", "Flies", "Ants", "Others"];

const FINDING_OPTIONS = [
  { value: "", label: "None" },
  { value: "Presence of foul odor", label: "Presence of foul odor" },
  { value: "Trash bins left uncovered", label: "Trash bins left uncovered" },
  { value: "Dirt/Organic Matter Present", label: "Dirt/Organic Matter Present" },
  { value: "Cracks and Crevices", label: "Cracks and Crevices" },
  { value: "Pest Entry Point / Open Windows", label: "Pest Entry Point / Open Windows" },
  { value: "Stagnant Water / Leaking Pipes", label: "Stagnant Water / Leaking Pipes" },
  { value: "Build-up of Old Stocks", label: "Build-up of Old Stocks" },
  { value: "Closed Room or Area Inaccessible", label: "Closed Room or Area Inaccessible" },
  { value: "Active termites found", label: "Active termites found" },
];

const RECOMMENDATION_OPTIONS = [
  { value: "", label: "None" },
  { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
  { value: "Keep trash bins covered", label: "Keep trash bins covered" },
  { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
  { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
  { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
  { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
  { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
  { value: "Make room available for inspection", label: "Make room available for inspection" },
  { value: "Install screens/weather strip", label: "Install screens/weather strip" },
];

export default function LocationCard({ control, register, index, removeLocation }) {
  // Watch object-shaped pests: { Roaches: number, Mosq: number, ... }
  const pestsObj =
    useWatch({ control, name: `locations.${index}.pests` }) || {};

  const totalCount = PEST_OPTIONS.reduce((sum, key) => {
    const n = Number(pestsObj?.[key] ?? 0);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  const findingsValue =
    useWatch({ control, name: `locations.${index}.findings` }) || "";

  const recommendationValue =
    useWatch({ control, name: `locations.${index}.recommendation` }) || "";

  // Pull errors for this row so we can show helper text
  const { errors } = useFormState({ control });
  const rowErrors = errors?.locations?.[index] || {};
  

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Location input */}
        <TextField
          label="Location"
          {...register(`locations.${index}.location`, {
            required: "Location is required",
          })}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
          error={!!rowErrors?.location}
          helperText={rowErrors?.location?.message || ""}
        />

        {/* Pest Count (mobile-friendly: two per row) */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Pest Count
        </Typography>
        {/* Hidden validator to enforce at least one pest > 0 */}
        <Controller
          name={`locations.${index}.pests`}
          control={control}
          rules={{
            validate: (val) => {
              const obj = val || {};
              const sum = PEST_OPTIONS.reduce((s, k) => {
                const n = Number(obj?.[k] ?? 0);
                return s + (isNaN(n) ? 0 : n);
              }, 0);
              return sum > 0 || "Enter at least one pest count (> 0)";
            },
          }}
          render={() => null}
        />
        <Grid container spacing={1}>
          {PEST_OPTIONS.map((label) => (
            <Grid item xs={6} key={label}>
              <TextField
                label={label}
                type="number"
                size="small"
                fullWidth
                placeholder="0"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  min: 0,
                }}
                {...register(`locations.${index}.pests.${label}`, {
                  valueAsNumber: true,
                })}
              />
            </Grid>
          ))}
        </Grid>

        {/* Total */}
        <Typography 
          variant="body2" 
          sx={{ mt: 1, fontWeight: "bold", color: rowErrors?.pests ? "error.main" : "inherit" }}
        >
          Total Pest Count: {totalCount}
        </Typography>
        {rowErrors?.pests && (
          <Typography variant="caption" color="error">
            {rowErrors?.pests?.message}
          </Typography>
        )}

        {/* Findings */}
        <Controller
          control={control}
          name={`locations.${index}.findings`}
          rules={{
            validate: (v) =>
              (v && v !== "") || "Please select a Finding or choose Other",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Findings"
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              error={!!rowErrors?.findings}
              helperText={rowErrors?.findings?.message || ""}
            >
              {FINDING_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          )}
        />
        {findingsValue === "Other" && (
          <TextField
            label="Other Findings"
            {...register(`locations.${index}.findings_other`, {
              validate: (v) =>
                findingsValue !== "Other" ||
                (String(v || "").trim().length > 0
                  ? true
                  : "Please specify other findings"),
            })}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            error={!!rowErrors?.findings_other}
            helperText={rowErrors?.findings_other?.message || ""}
          />
        )}

        {/* Recommendation */}
        <Controller
          control={control}
          name={`locations.${index}.recommendation`}
          rules={{
            validate: (v) =>
              (v && v !== "") || "Please select a Recommendation or choose Other",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Recommendation"
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              error={!!rowErrors?.recommendation}
              helperText={rowErrors?.recommendation?.message || ""}
            >
              {RECOMMENDATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          )}
        />
        {recommendationValue === "Other" && (
          <TextField
            label="Other Recommendation"
            {...register(`locations.${index}.recommendation_other`, {
              validate: (v) =>
                recommendationValue !== "Other" ||
                (String(v || "").trim().length > 0
                  ? true
                  : "Please specify other recommendation"),
            })}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            error={!!rowErrors?.recommendation_other}
            helperText={rowErrors?.recommendation_other?.message || ""}
          />
        )}

        {/* Remove this location */}
        <Box textAlign="right" sx={{ mt: 1.5 }}>
          <Button color="error" size="small" onClick={() => removeLocation(index)}>
            Remove Location
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
